// Dana live call (Phase 3) — LiveKit room lifecycle for the Assistant panel.
// The backend mints a room-scoped token (entitlement re-checked server-side);
// this hook connects, publishes the mic, plays Dana's audio, surfaces call
// state, and receives proposal cards over the data channel.
import { useCallback, useEffect, useRef, useState } from 'react';
import { withAuth } from '@/lib/hrApi';
import type { VoiceProposal } from '@/lib/danaVoiceApi';

const client = withAuth(import.meta.env.VITE_AI_URL || 'http://localhost:8001');

export type CallState = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'error';

export function useDanaCall(onProposal: (p: VoiceProposal) => void) {
  const [state, setState] = useState<CallState>('idle');
  const [danaSpeaking, setDanaSpeaking] = useState(false);
  const roomRef = useRef<any>(null);
  const audioElsRef = useRef<HTMLAudioElement[]>([]);
  const onProposalRef = useRef(onProposal);
  onProposalRef.current = onProposal;

  const endCall = useCallback(() => {
    try { roomRef.current?.disconnect(); } catch { /* already gone */ }
    roomRef.current = null;
    audioElsRef.current.forEach((el) => { el.pause(); el.remove(); });
    audioElsRef.current = [];
    setDanaSpeaking(false);
    setState('idle');
  }, []);

  useEffect(() => () => endCall(), [endCall]);

  const startCall = useCallback(async () => {
    if (roomRef.current) { endCall(); return; }
    setState('connecting');
    try {
      const form = new FormData();
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) form.append('timezone', tz);
      } catch { /* zone-less */ }
      const { data } = await client.post('/api/v1/ai/voice/session', form);

      const { Room, RoomEvent, Track } = await import('livekit-client');
      const room = new Room();
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: any) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          el.autoplay = true;
          document.body.appendChild(el);
          audioElsRef.current.push(el);
        }
      });
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: any[]) => {
        setDanaSpeaking(speakers.some((s) => s.identity !== room.localParticipant.identity));
      });
      room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload));
          if (msg?.type === 'dana_proposal' && msg.proposal) onProposalRef.current(msg.proposal);
        } catch { /* not ours */ }
      });
      room.on(RoomEvent.Reconnecting, () => setState('reconnecting'));
      room.on(RoomEvent.Reconnected, () => setState('live'));
      room.on(RoomEvent.Disconnected, () => endCall());

      await room.connect(data.url, data.token);
      await room.localParticipant.setMicrophoneEnabled(true);
      setState('live');
    } catch (e) {
      endCall();
      setState('error');
      setTimeout(() => setState('idle'), 100);
      throw e;
    }
  }, [endCall]);

  return { state, danaSpeaking, startCall, endCall };
}
